import type { AuditHandoffAction } from '@compass/client/types/audit';
import { getOutputComponent, getPreviewComponent } from '@components/fields';
import { ReasoningTooltip, ReviewButton } from '@components/ui/review-button';
import { Check, ThumbsDown, ThumbsUp, Undo2, X } from 'lucide-react';
import React, { useMemo, useState } from 'react';

import type { FieldEvidence } from '@/components/fields/types';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
// Re-importing Badge above; used in the recommendation pill rendered next to
// the Reasoning label below.
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import type { FieldGroup } from '@/components/ui/field-group-accordion';
import { NestedFieldAccordion } from '@/components/ui/nested-field-accordion';
import { Textarea } from '@/components/ui/textarea';
import { SimpleTooltipV2 } from '@/components/ui/tooltip';
import type { FormField } from '@/types/formSchema';
import {
  computeLeafDiffs,
  expandLeafCorrections,
  getValueAtPath,
  type LeafCorrection,
  resolveLeafBeforeValue,
} from '@/utils/auditCorrections';
import { getCallSourceId } from '@/utils/evidence';
import { type FieldEnumMap, formatFieldValue } from '@/utils/fields';
import { hasOnlyHiddenFields } from '@/utils/fieldVisibility';
import { handoffFieldLabel, outputPathBreadcrumb } from '@/utils/handoffField';
import { formatFieldName } from '@/utils/string';

type AuditFeedbackAnswer = 'yes' | 'no';

interface AuditCorrectionInfo {
  /** "KEEP" | "CHANGE" | "ADD" | "REMOVE" — drives the pill and before/after rendering. */
  action?: string;
  /** Audit's suggested value for this field (typed; coerced to string for display). */
  corrected_value?: unknown;
  /**
   * The value the audit compared against. The caller has always supplied this; it was
   * simply not declared here, so the leaf diff fell back to the field's LIVE value.
   *
   * That fallback is wrong for a SPARSE correction, which is what the pa_status auditor
   * writes: `original_value: [{code}]` / `corrected_value: [{code, prior_auth_status:
   * {prior_authorization_required: "yes"}}]` — identity plus the one touched leaf, not
   * the whole entity. Diffing that against the live entity reports every leaf the
   * correction omits as a deletion. Diff the pair the writer recorded instead.
   */
  original_value?: unknown;
  /** Audit reasoning (shown in edit form as helper text). */
  reasoning?: string;
}

type BadgeVariant = 'default' | 'destructive' | 'success' | 'warning' | 'progress';

interface AuditorOutputFieldsProps {
  groups: FieldGroup[];
  getCorrection: (fieldPath: string) => AuditCorrectionInfo | undefined;
  /** Non-KEEP corrections on nested leaf paths, grouped under this root field. */
  getLeafCorrections?: (fieldPath: string) => LeafCorrection[] | undefined;
  getValue: (fieldPath: string) => unknown;
  /**
   * True when the selected audit carries an `output_snapshot`, i.e. `getValue` is
   * returning the output as that audit's auditor actually read it. Leaf rows then take
   * their "before" from that value alone and ignore a correction's `original_value`:
   * the snapshot is the one baseline, and consulting a second source could show a
   * before-value from a different moment. Rows written before snapshots existed pass
   * false and keep the old per-correction fallback.
   */
  snapshotBacked?: boolean;
  fieldEnumMap?: FieldEnumMap;
  changedFieldCount?: number;
  showChangedOnly?: boolean;
  onToggleChangedOnly?: () => void;
  /** Controlled list of expanded group indices (as strings). The reserved
   * value `"reasoning"` controls the Reasoning accordion. */
  expanded?: string[];
  onExpandedChange?: (value: string[]) => void;
  /** Optional reasoning content; when provided renders an accordion item at the top. */
  reasoning?: React.ReactNode;
  /** Handoff actions the AI auditor flagged; when present renders a view-only
   * section above Reasoning. */
  handoffActions?: AuditHandoffAction[];
  /**
   * AI-extraction context, the same three objects the Summary and call-output views pass
   * to FieldGroupAccordion. Without them a nested field renders no chain of thought and no
   * AI-suggested value here, while the identical field shows both on Summary (UAT-1571).
   * View-only in this tab: `onAcceptExtracted` / `isHumanAudit` are deliberately NOT
   * forwarded, so the suggestion is displayed but never acceptable from an audit.
   */
  outputMetadata?: { fields?: Record<string, unknown> };
  extractedData?: Record<string, unknown>;
  extractedMetadata?: { fields?: Record<string, unknown> };
  /** Restricts the suggestion chip to extractions from the call being viewed. */
  currentCallId?: string;
  onNavigateToCall?: (callId: string, evidenceTimeStart?: string, evidenceTimeEnd?: string) => void;
  onNavigateToAudit?: (auditId: string) => void;
  /**
   * FULL output schema groups, used only to resolve handoff action field labels.
   * Must not be the filtered set `groups` may be: callers narrow `groups` with the
   * "fields changed" filter, and resolving labels against that would drop the label
   * for any field the filter hides — and change it as the filter is toggled.
   * Falls back to `groups` when omitted.
   */
  schemaGroups?: FieldGroup[];
  /** Audit recommendation (e.g. "ESCALATE_TO_HUMAN", "AUTO_APPROVE") — when
   * provided renders a colored pill beside the Reasoning label. */
  recommendation?: string;
  /** Called when the user submits 👍 / 👎 feedback on the audit. */
  onSubmitFeedback?: (answer: AuditFeedbackAnswer, note: string) => void;
  /** Workflow type — scopes which custom array components are used (BI-1442) */
  workflowType?: string;
  /**
   * When provided, every changed row offers Revert: writes `value` (the field's pre-audit
   * value, exactly what the row shows on the left of the arrow) back at `fieldPath`.
   * Omitted when the viewer may not edit the case output, which hides the affordance.
   */
  onRevertField?: (fieldPath: string, value: unknown) => Promise<void> | void;
  /**
   * Whether `fieldPath` has already been reverted. Owned by the page, not by the Revert
   * control's own state, so the acknowledgement survives the panel unmounting — switching
   * tabs and coming back must not offer Revert again on a field already reverted.
   */
  isFieldReverted?: (fieldPath: string) => boolean;
}

interface RowProps {
  field: FormField;
  value: unknown;
  correction: AuditCorrectionInfo | undefined;
  leafCorrections?: LeafCorrection[];
  /** See AuditorOutputFieldsProps.snapshotBacked. */
  snapshotBacked?: boolean;
  fieldEnumMap: FieldEnumMap;
  /** Scopes component registry lookups to this workflow (BI-1442) */
  workflowType?: string;
  /** FULL schema groups — resolve nested leaf labels against the unfiltered schema. */
  schemaGroups: FieldGroup[];
  onRevertField?: (fieldPath: string, value: unknown) => Promise<void> | void;
  isFieldReverted?: (fieldPath: string) => boolean;
  /**
   * Suppresses this row's own label. A schema that names a nested field after its own
   * group (medbv's `drug` / `procedure` are both grouped and labelled "Drug Details" /
   * "Procedure Details") otherwise renders the heading twice — group, then an identical
   * field label, then the codes. FieldGroupAccordion drops the inner one for the same
   * reason; this is that rule for the audit tab.
   */
  hideLabel?: boolean;
  /** AI-extraction context for nested leaves; see AuditorOutputFieldsProps. */
  outputMetaNode?: Record<string, unknown>;
  extractedDataNode?: Record<string, unknown>;
  extractedMetaNode?: Record<string, unknown>;
  currentCallId?: string;
  onNavigateToCall?: (callId: string, evidenceTimeStart?: string, evidenceTimeEnd?: string) => void;
  onNavigateToAudit?: (auditId: string) => void;
}

const RECOMMENDATION_LABEL: Record<string, string> = {
  ESCALATE_TO_HUMAN: 'Escalate to Human',
  AUTO_APPROVE: 'Auto Approve',
};

function recommendationPill(
  value: string | undefined,
): { text: string; variant: BadgeVariant } | null {
  if (!value) return null;
  const normalized = value.toUpperCase();
  const text = RECOMMENDATION_LABEL[normalized] ?? value;
  if (normalized === 'ESCALATE_TO_HUMAN') return { text, variant: 'warning' };
  if (normalized === 'AUTO_APPROVE') return { text, variant: 'success' };
  return { text, variant: 'default' };
}

const EMPTY_VALUE_PLACEHOLDER = '—';
const EMPTY_STRING_DISPLAY = '""';

// Show the full value (no truncation) in the audit panel — these rows expand
// the case's structured fields, not a compact table cell.
const MAX_PREVIEW_ITEMS = 100;

/** Coerce a value into the array shape the preview components expect. */
function toPreviewData(value: unknown): unknown[] {
  if (Array.isArray(value)) return value;
  if (value === null || value === undefined) return [];
  return [value];
}

/**
 * Render a custom-component field (drug, procedure, sites_of_care, j_code, …)
 * using its registered preview component instead of the generic string
 * formatter — which would otherwise stringify the nested object to
 * "[object Object]". Mirrors the `action` before → after layout used for
 * primitive fields.
 */
const CustomAuditValue: React.FC<{
  PreviewComponent: React.ComponentType<{ data: unknown[]; maxItems?: number }>;
  action: string;
  value: unknown;
  correctedValue: unknown;
}> = ({ PreviewComponent, action, value, correctedValue }) => {
  const renderPreview = (v: unknown): React.ReactNode => {
    const data = toPreviewData(v);
    if (data.length === 0) {
      return <span className="text-sm text-muted-foreground">{EMPTY_VALUE_PLACEHOLDER}</span>;
    }
    return <PreviewComponent data={data} maxItems={MAX_PREVIEW_ITEMS} />;
  };

  const emptySide = <span className="text-sm text-muted-foreground">{EMPTY_STRING_DISPLAY}</span>;

  switch (action) {
    case 'CHANGE':
      return (
        <div className="flex items-center gap-2 flex-wrap text-sm">
          {renderPreview(value)}
          <span className="text-muted-foreground">→</span>
          {renderPreview(correctedValue)}
        </div>
      );
    case 'ADD':
      return (
        <div className="flex items-center gap-2 flex-wrap text-sm">
          {emptySide}
          <span className="text-muted-foreground">→</span>
          {renderPreview(correctedValue)}
        </div>
      );
    case 'REMOVE':
      return (
        <div className="flex items-center gap-2 flex-wrap text-sm">
          {renderPreview(value)}
          <span className="text-muted-foreground">→</span>
          {emptySide}
        </div>
      );
    default:
      // KEEP and everything else: show value directly (like the summary view).
      return renderPreview(value);
  }
};

/** Humanize a leaf path (e.g. "[0].coverage.copay" → "Coverage › Copay"). The
 * first array index is dropped (single-item common case); later indices show as
 * "#n" so multiple instances stay distinguishable. */
function humanizeLeafPath(path: string): string {
  const parts: string[] = [];
  const regex = /([^.[\]]+)|\[(\d+)\]/g;
  let m: RegExpExecArray | null;
  while ((m = regex.exec(path)) !== null) {
    if (m[1] !== undefined) {
      parts.push(formatFieldName(m[1]));
    } else if (m[2] !== undefined) {
      const idx = Number(m[2]);
      if (idx > 0) parts.push(`#${idx + 1}`);
    }
  }
  return parts.join(' › ');
}

/**
 * Display label for one changed leaf under a root field row.
 *
 * Resolves the leaf against the SCHEMA, so a nested correction reads exactly like a
 * handoff action does — `Drug Details · J1234 › Drug Coverage › Copay` — with the entity
 * instance named from the case's own data. The auditor emits one correction per instance
 * (`drug.0…` through `drug.4…`), and those must not all collapse to the same label.
 *
 * `relPath` is the leaf path RELATIVE to the root, in either form the two callers produce:
 * `[0].coverage.copay` (correction records) or `0.coverage.copay` (flattened diff).
 * Falls back to the humanized raw path when the schema can't resolve it, so nothing ever
 * renders label-less.
 */
/** `drug` + `[0].coverage.copay` (or `0.coverage.copay`) → `drug[0].coverage.copay`. */
function leafFieldPath(rootId: string, relPath: string): string {
  return relPath.startsWith('[') ? `${rootId}${relPath}` : `${rootId}.${relPath}`;
}

function leafLabel(
  schemaGroups: FieldGroup[],
  rootId: string,
  rootValue: unknown,
  relPath: string,
): string {
  const breadcrumb = outputPathBreadcrumb(
    schemaGroups,
    { [rootId]: rootValue },
    leafFieldPath(rootId, relPath),
  );
  if (breadcrumb) return breadcrumb;
  return humanizeLeafPath(relPath) || relPath;
}

function formatLeafValue(v: unknown): React.ReactNode {
  if (v === null || v === undefined || v === '') {
    return <span className="text-muted-foreground">{EMPTY_STRING_DISPLAY}</span>;
  }
  if (typeof v === 'object') return JSON.stringify(v);
  return String(v);
}

/**
 * Flat, single-level diff for a nested/custom field (e.g. `drug`). Instead of
 * re-rendering the whole nested UI, list only the leaves that actually changed as
 * `Label: before → after` rows (BI-1246).
 */
const NestedAuditDiff: React.FC<{
  original: unknown;
  corrected: unknown;
  /** Root field + schema, so each changed leaf reads as a schema breadcrumb. */
  rootId: string;
  schemaGroups: FieldGroup[];
  onRevertField?: (fieldPath: string, value: unknown) => Promise<void> | void;
  isFieldReverted?: (fieldPath: string) => boolean;
}> = ({ original, corrected, rootId, schemaGroups, onRevertField, isFieldReverted }) => {
  const diffs = React.useMemo(() => computeLeafDiffs(original, corrected), [original, corrected]);
  // Entity identity (`code` / `name`) for the breadcrumb comes from whichever side of the
  // diff still has the element: an entity the audit ADDED exists only in `corrected`, one
  // it removed only in `original`. Without this, a changed leaf on the second drug reads
  // as a bare position and the auditor can't tell which code it belongs to.
  const identityValue = React.useMemo(() => {
    if (!Array.isArray(original) && !Array.isArray(corrected)) return original;
    const before = Array.isArray(original) ? original : [];
    const after = Array.isArray(corrected) ? corrected : [];
    return Array.from(
      { length: Math.max(before.length, after.length) },
      (_, i) => before[i] ?? after[i],
    );
  }, [original, corrected]);
  if (diffs.length === 0) {
    return <span className="text-sm text-muted-foreground">{EMPTY_VALUE_PLACEHOLDER}</span>;
  }
  return (
    <div className="space-y-2">
      {diffs.map(d => (
        <LeafDiffRow
          key={d.path}
          label={leafLabel(schemaGroups, rootId, identityValue, d.path)}
          before={formatLeafValue(d.before)}
          after={formatLeafValue(d.after)}
          onRevert={
            onRevertField ? () => onRevertField(leafFieldPath(rootId, d.path), d.before) : undefined
          }
          reverted={isFieldReverted?.(leafFieldPath(rootId, d.path))}
        />
      ))}
    </div>
  );
};

/**
 * Revert one changed field to the value it held before the audit overrode it.
 *
 * Two-step by design — the write is immediate and there is no undo, so the button only
 * arms the action and a tick / cross beside the prompt commits or abandons it. Once
 * reverted the row says so: the audit record still shows `before → after` (it is a
 * historical record and does not change), so without that acknowledgement the click
 * would look like it did nothing.
 *
 * `reverted` is that acknowledgement as the PAGE remembers it. The local flag alone dies
 * with the component, so leaving the audit tab and returning re-offered Revert on a field
 * already written back; the page-owned flag keeps the row honest across remounts.
 *
 * HIPAA: the value is PHI — it is passed to the persist endpoint and never logged.
 */
const RevertControl: React.FC<{
  onRevert: () => Promise<void> | void;
  reverted?: boolean;
}> = ({ onRevert, reverted }) => {
  const [isConfirming, setIsConfirming] = useState(false);
  const [isReverting, setIsReverting] = useState(false);
  const [locallyReverted, setLocallyReverted] = useState(false);

  if (reverted || locallyReverted) {
    return <span className="text-[11px] font-medium text-muted-foreground">Reverted</span>;
  }

  if (!isConfirming) {
    return (
      <SimpleTooltipV2 content="Revert this field to its pre-audit value" side="left">
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="h-6 shrink-0 gap-1 px-1.5 text-[11px] font-medium text-muted-foreground hover:text-foreground"
          aria-label="Revert this field to its pre-audit value"
          onClick={() => setIsConfirming(true)}
        >
          <Undo2 className="h-3 w-3" />
          Revert
        </Button>
      </SimpleTooltipV2>
    );
  }

  const commit = async () => {
    setIsReverting(true);
    try {
      await onRevert();
      setLocallyReverted(true);
    } catch {
      // The caller surfaces its own error toast; leave the row revertable for a retry
      // rather than claiming a revert that did not land.
    } finally {
      setIsReverting(false);
      setIsConfirming(false);
    }
  };

  return (
    <span className="inline-flex shrink-0 items-center gap-1.5">
      <span className="text-[11px] text-muted-foreground">
        Revert to the value before the audit?
      </span>
      {/* Tick and cross each get their own bordered box — one shared container read as a
          single control and made the two easy to mis-click. */}
      <SimpleTooltipV2 content="Revert the field" side="left">
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="h-6 w-6 shrink-0 p-0 text-muted-foreground hover:text-foreground"
          disabled={isReverting}
          aria-label="Confirm the revert"
          onClick={commit}
        >
          <Check className="h-3.5 w-3.5" />
        </Button>
      </SimpleTooltipV2>
      <SimpleTooltipV2 content="Keep the audited value" side="left">
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="h-6 w-6 shrink-0 p-0 text-muted-foreground hover:text-destructive"
          disabled={isReverting}
          aria-label="Keep the audited value"
          onClick={() => setIsConfirming(false)}
        >
          <X className="h-3.5 w-3.5" />
        </Button>
      </SimpleTooltipV2>
    </span>
  );
};

/** One `Label: before → after` leaf row, shared by both nested diff views. */
const LeafDiffRow: React.FC<{
  label: string;
  before: React.ReactNode;
  after: React.ReactNode;
  reasoning?: string;
  /** When set, the row offers Revert (see {@link RevertControl}). */
  onRevert?: () => Promise<void> | void;
  /** Page-remembered: this row's field was already reverted. */
  reverted?: boolean;
  /**
   * Extraction evidence behind this leaf's persisted value. Renders the same
   * Review + chain-of-thought pair `EditableField` and `NestedFieldAccordion`
   * put on every other field; a CHANGED nested leaf had neither (LA-6912),
   * because this row is the only view those two components never reach.
   */
  evidence?: FieldEvidence[];
  sourceCallId?: string;
  onNavigateToCall?: (callId: string, evidenceTimeStart?: string, evidenceTimeEnd?: string) => void;
  onNavigateToAudit?: (auditId: string) => void;
}> = ({
  label,
  before,
  after,
  reasoning,
  onRevert,
  reverted,
  evidence,
  sourceCallId,
  onNavigateToCall,
  onNavigateToAudit,
}) => (
  <div className="space-y-0.5">
    <div className="flex items-center gap-1">
      <p className="text-[11px] font-medium text-muted-foreground">{label}</p>
      {!!evidence?.length && onNavigateToCall && (
        <>
          <ReviewButton
            sourceCallId={sourceCallId}
            evidence={evidence}
            onNavigateToCall={onNavigateToCall}
            onNavigateToAudit={onNavigateToAudit}
            className="text-xs"
          />
          <ReasoningTooltip evidence={evidence} />
        </>
      )}
    </div>
    {/* Values left, Revert pinned RIGHT — the control keeps one column down the panel
        instead of landing at a different spot per row's value length. */}
    <div className="flex items-start justify-between gap-2">
      <div className="flex min-w-0 flex-wrap items-center gap-2 text-sm break-words">
        {before}
        <span className="text-muted-foreground">→</span>
        {after}
      </div>
      {onRevert && <RevertControl onRevert={onRevert} reverted={reverted} />}
    </div>
    {reasoning && <p className="text-[11px] text-muted-foreground italic">{reasoning}</p>}
  </div>
);

/**
 * Leaf-level corrections rendered under their root field row. Unlike
 * NestedAuditDiff, rows come straight from the audit's correction records —
 * each corrected_value is a scalar leaf, not a whole nested value. The
 * "before" side falls back to resolving the leaf path against the row's
 * pre-audit value because AI-audit corrections carry no original_value.
 */
const LeafCorrectionRows: React.FC<{
  corrections: LeafCorrection[];
  rootValue: unknown;
  /** See AuditorOutputFieldsProps.snapshotBacked. */
  snapshotBacked?: boolean;
  rootId: string;
  schemaGroups: FieldGroup[];
  onRevertField?: (fieldPath: string, value: unknown) => Promise<void> | void;
  isFieldReverted?: (fieldPath: string) => boolean;
  /** This root field's output metadata subtree, for per-leaf evidence (LA-6912). */
  outputMetaNode?: Record<string, unknown>;
  onNavigateToCall?: (callId: string, evidenceTimeStart?: string, evidenceTimeEnd?: string) => void;
  onNavigateToAudit?: (auditId: string) => void;
}> = ({
  corrections,
  rootValue,
  snapshotBacked,
  rootId,
  schemaGroups,
  onRevertField,
  isFieldReverted,
  outputMetaNode,
  onNavigateToCall,
  onNavigateToAudit,
}) => (
  <div className="space-y-2">
    {/* Container-valued corrections (human audit records `drug[0]` carrying an object)
        become one row per changed leaf; scalar corrections pass through untouched. */}
    {expandLeafCorrections(corrections).map((c, i) => {
      // The pre-audit value, resolved once: the row shows it and Revert writes it back.
      // Snapshot-backed audits read it from the snapshot alone; see resolveLeafBeforeValue.
      const beforeValue = resolveLeafBeforeValue({ ...c, rootValue, snapshotBacked });
      // Same indexing the value uses, against the metadata subtree rather than the data.
      const leafMeta = getValueAtPath(outputMetaNode, c.path) as
        | { evidence?: FieldEvidence[]; source_id?: string; source_type?: 'call' | 'ai_audit' }
        | undefined;
      return (
        <LeafDiffRow
          key={`${c.path}-${i}`}
          evidence={leafMeta?.evidence}
          sourceCallId={getCallSourceId(leafMeta)}
          onNavigateToCall={onNavigateToCall}
          onNavigateToAudit={onNavigateToAudit}
          label={leafLabel(schemaGroups, rootId, rootValue, c.path)}
          before={
            c.action === 'ADD' ? (
              <span className="text-muted-foreground">{EMPTY_VALUE_PLACEHOLDER}</span>
            ) : (
              formatLeafValue(beforeValue)
            )
          }
          after={
            c.action === 'REMOVE' ? (
              <span className="text-muted-foreground">{EMPTY_VALUE_PLACEHOLDER}</span>
            ) : (
              formatLeafValue(c.corrected_value)
            )
          }
          reasoning={c.reasoning}
          onRevert={
            onRevertField
              ? () => onRevertField(leafFieldPath(rootId, c.path), beforeValue)
              : undefined
          }
          reverted={isFieldReverted?.(leafFieldPath(rootId, c.path))}
        />
      );
    })}
  </div>
);

/**
 * Whether a group's single nested field repeats the group's own heading — medbv's `drug`
 * and `procedure` are grouped AND labelled "Drug Details" / "Procedure Details", which
 * rendered as `Drug Details -> Drug Details -> <items>`. Same rule (and same conditions)
 * as FieldGroupAccordion's `dropsRedundantHeading`: only when the group holds nothing but
 * that one nested field, so no scalar sibling is left without a heading to sit under.
 */
const hasRedundantHeading = (group: FieldGroup): boolean => {
  if (!group.label) return false;
  const shown = group.fields.filter(field => !field.hidden);
  const nested = shown.filter(isNestedField);
  return (
    nested.length === 1 &&
    shown.length === 1 &&
    nested[0].label.trim().toLowerCase() === group.label.trim().toLowerCase()
  );
};

/** Array / object fields — the ones NestedFieldAccordion renders. Mirrors
 * `separateFieldTypes`, which splits the same way in FieldGroupAccordion. */
const isNestedField = (field: FormField): boolean =>
  field.type === 'array' || field.type === 'object';

const AuditorFieldRow: React.FC<RowProps> = ({
  field,
  value,
  correction,
  leafCorrections,
  snapshotBacked,
  fieldEnumMap,
  workflowType,
  schemaGroups,
  onRevertField,
  isFieldReverted,
  hideLabel,
  outputMetaNode,
  extractedDataNode,
  extractedMetaNode,
  currentCallId,
  onNavigateToCall,
  onNavigateToAudit,
}) => {
  const action = (correction?.action ?? '').toUpperCase();

  // Custom-component fields (drug, procedure, sites_of_care, j_code, …) would
  // otherwise stringify to "[object Object]". Prefer the rich output component
  // (collapsible nested schema — coverage, prior auth, denial, …, same as the
  // summary view); fall back to the compact badge preview for input-only fields
  // (j_code/cpt_code/diagnosis have a preview but no output component); else use
  // the generic string formatter.
  const OutputComponent = getOutputComponent(field.id, workflowType);
  const PreviewComponent = getPreviewComponent(field.id, workflowType);

  // Nested/custom fields (drug, procedure, …) with an actual correction show a
  // flat leaf-level diff of only what changed (BI-1246) — not the entire nested
  // UI. KEEP / uncorrected fields still render the full rich output component.
  const hasNestedDiff = action === 'CHANGE' || action === 'ADD' || action === 'REMOVE';

  // Which fields get that leaf-level diff. A custom output component was the original
  // proxy for "this is an entity container", but it only ever covered medbv /
  // unifiedmedbv — so every pa_status entity list fell past this branch and rendered the
  // WHOLE tree under a correction, leaving the auditor to hunt for what the audit
  // actually touched (LA-1781). The real condition is the shape of the field, so ask
  // that directly: a schema-structured container is exactly what NestedFieldAccordion
  // renders as a tree, and exactly what a whole-block render is unreadable for.
  //
  // `fields.length` deliberately excludes a repeated SCALAR (a `type: 'array'` with no
  // sub-fields, e.g. prior_authorization_submission_methods). Those hold the answer
  // itself, so one `Fax → Mail` line reads better than per-index leaf rows.
  const isEntityContainer = isNestedField(field) && (field.fields?.length ?? 0) > 0;

  // Revert writes the field's pre-audit value back — `value` here, which is exactly what
  // the row renders on the left of the arrow. For an ADD there was nothing before, so
  // reverting CLEARS the field. Container fields whose diff is rendered leaf by leaf get
  // a control per LEAF instead (see NestedAuditDiff / LeafCorrectionRows), so this is only
  // for rows that show one whole-field before → after.
  const revert =
    onRevertField && hasNestedDiff ? (
      <RevertControl
        onRevert={() => onRevertField(field.id, action === 'ADD' ? undefined : value)}
        reverted={isFieldReverted?.(field.id)}
      />
    ) : null;

  let valueNode: React.ReactNode;
  if (hasNestedDiff && (OutputComponent || isEntityContainer)) {
    valueNode = (
      <NestedAuditDiff
        // The correction's own before/after pair when it recorded one — see
        // `original_value`. Only a correction without it diffs against the live value.
        original={correction?.original_value ?? value}
        corrected={correction?.corrected_value}
        rootId={field.id}
        schemaGroups={schemaGroups}
        onRevertField={onRevertField}
        isFieldReverted={isFieldReverted}
      />
    );
  } else if (!hasNestedDiff && leafCorrections && leafCorrections.length > 0) {
    // Corrections on nested leaf paths (procedure.0.coverage.x) — no root-level
    // correction exists, so render the leaf diffs from the correction records.
    valueNode = (
      <LeafCorrectionRows
        corrections={leafCorrections}
        rootValue={value}
        snapshotBacked={snapshotBacked}
        rootId={field.id}
        schemaGroups={schemaGroups}
        onRevertField={onRevertField}
        isFieldReverted={isFieldReverted}
        outputMetaNode={outputMetaNode}
        onNavigateToCall={onNavigateToCall}
        onNavigateToAudit={onNavigateToAudit}
      />
    );
  } else if (OutputComponent) {
    valueNode = <OutputComponent data={toPreviewData(value)} fieldId={field.id} isHumanAudit />;
  } else if (isNestedField(field)) {
    // A nested field with NO registered output component — every pa_status_medbv entity
    // list, since OUTPUT_COMPONENTS only covers medbv / unifiedmedbv. It used to fall
    // through to the string formatter and render "[object Object]". The generic accordion
    // is what Summary and the call-output panel already use for these, so the audit tab
    // now shows the identical tree, including the per-leaf chain of thought and AI
    // suggestion the meta nodes carry.
    //
    // Reached only when the field is UNCORRECTED (or its correction is a KEEP): a
    // corrected container takes the leaf-diff branch above. Showing the full tree is
    // right here — with nothing changed, the value IS the thing to read.
    valueNode = (
      <NestedFieldAccordion
        field={field}
        data={value}
        path={field.id}
        depth={1}
        isEditable={false}
        outputMetaNode={outputMetaNode}
        extractedDataNode={extractedDataNode}
        extractedMetaNode={extractedMetaNode}
        currentCallId={currentCallId}
        onNavigateToCall={onNavigateToCall}
        onNavigateToAudit={onNavigateToAudit}
      />
    );
  } else if (PreviewComponent) {
    valueNode = (
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <CustomAuditValue
            PreviewComponent={PreviewComponent}
            action={action}
            value={value}
            correctedValue={correction?.corrected_value}
          />
        </div>
        {revert}
      </div>
    );
  } else {
    const displayValue = formatFieldValue(value, field.id, '', fieldEnumMap);
    const before = displayValue || EMPTY_STRING_DISPLAY;
    const after =
      correction?.corrected_value != null
        ? String(correction.corrected_value)
        : EMPTY_STRING_DISPLAY;

    // KEEP (or no correction) — just render the value as in the summary view.
    // CHANGE / ADD / REMOVE — render inline as `before → after`.
    switch (action) {
      case 'CHANGE':
        valueNode = (
          <div className="flex items-start justify-between gap-2">
            <span className="flex min-w-0 flex-wrap items-center gap-2 text-sm break-words">
              {before} <span className="text-muted-foreground">→</span> {after}
            </span>
            {revert}
          </div>
        );
        break;
      case 'ADD':
        valueNode = (
          <div className="flex items-start justify-between gap-2">
            <span className="flex min-w-0 flex-wrap items-center gap-2 text-sm break-words">
              <span className="text-muted-foreground">{EMPTY_STRING_DISPLAY}</span>
              <span className="text-muted-foreground">→</span> {after}
            </span>
            {revert}
          </div>
        );
        break;
      case 'REMOVE':
        valueNode = (
          <div className="flex items-start justify-between gap-2">
            <span className="flex min-w-0 flex-wrap items-center gap-2 text-sm break-words">
              {before} <span className="text-muted-foreground">→</span>
              <span className="text-muted-foreground">{EMPTY_STRING_DISPLAY}</span>
            </span>
            {revert}
          </div>
        );
        break;
      default:
        // KEEP and everything else: show value directly (like the case summary view).
        valueNode = (
          <p className="text-sm break-words">{displayValue || EMPTY_VALUE_PLACEHOLDER}</p>
        );
    }
  }

  return (
    <div className="py-3 space-y-2">
      {!hideLabel && <p className="text-xs font-semibold text-foreground">{field.label}</p>}

      <div>{valueNode}</div>

      {correction?.reasoning && (
        <p className="text-[11px] text-muted-foreground italic">{correction.reasoning}</p>
      )}
    </div>
  );
};

export const AuditorOutputFields: React.FC<AuditorOutputFieldsProps> = ({
  groups,
  getCorrection,
  getLeafCorrections,
  getValue,
  snapshotBacked,
  fieldEnumMap = {},
  changedFieldCount = 0,
  showChangedOnly = false,
  onToggleChangedOnly,
  expanded: expandedProp,
  onExpandedChange,
  reasoning,
  handoffActions,
  schemaGroups,
  recommendation,
  onSubmitFeedback,
  workflowType,
  onRevertField,
  isFieldReverted,
  outputMetadata,
  extractedData,
  extractedMetadata,
  currentCallId,
  onNavigateToCall,
  onNavigateToAudit,
}) => {
  const recPill = recommendationPill(recommendation);
  const [internalExpanded, setInternalExpanded] = useState<string[]>(() =>
    groups.map((_, i) => i.toString()),
  );
  const expanded = expandedProp ?? internalExpanded;
  const setExpanded = onExpandedChange ?? setInternalExpanded;

  const [feedbackAnswer, setFeedbackAnswer] = useState<AuditFeedbackAnswer | null>(null);
  const [feedbackNote, setFeedbackNote] = useState('');

  // Resolve each handoff action's raw short field name to its schema label once —
  // handoffFieldLabel walks every output leaf, so it must not run per render per row.
  // Resolved against the FULL schema, never the filtered `groups`, so a label neither
  // disappears nor changes when the "fields changed" filter is toggled.
  const labelGroups = schemaGroups ?? groups;
  const handoffLabels = useMemo(
    () => (handoffActions ?? []).map(action => handoffFieldLabel(labelGroups, action.field)),
    [handoffActions, labelGroups],
  );

  if (groups.length === 0) {
    return <p className="text-xs text-muted-foreground">No output fields.</p>;
  }

  return (
    <div className="space-y-3">
      {handoffActions && handoffActions.length > 0 && (
        <Accordion
          type="multiple"
          value={expanded}
          onValueChange={setExpanded}
          className="space-y-3"
        >
          <AccordionItem value="handoff" className="bg-card border rounded-lg">
            <AccordionTrigger className="px-3 py-3 text-sm font-semibold text-foreground hover:bg-muted rounded-lg data-[state=open]:rounded-b-none gap-2">
              <span>Handoff actions</span>
            </AccordionTrigger>
            <AccordionContent className="px-3 pb-3">
              {/* View-only: the AI auditor's handoff actions, capped at the same
                  height as the Reasoning body so a long list scrolls in place.
                  Case-level actions come first and render note-only (no field name). */}
              <ul className="max-h-60 space-y-2 overflow-y-auto p-1">
                {handoffActions.map((action, index) => (
                  <li key={`${action.field ?? ''}-${index}`} className="flex flex-col gap-0.5">
                    {/* Schema label, not the auditor's raw short field name. */}
                    {handoffLabels[index] && (
                      <span className="break-words text-xs font-medium text-foreground">
                        {handoffLabels[index]}
                      </span>
                    )}
                    {action.note && (
                      <span className="whitespace-pre-wrap break-words text-sm text-muted-foreground">
                        {action.note}
                      </span>
                    )}
                  </li>
                ))}
              </ul>
            </AccordionContent>
          </AccordionItem>
        </Accordion>
      )}
      {reasoning && (
        <Accordion
          type="multiple"
          value={expanded}
          onValueChange={setExpanded}
          className="space-y-3"
        >
          <AccordionItem value="reasoning" className="bg-card border rounded-lg">
            <AccordionTrigger className="px-3 py-3 text-sm font-semibold text-foreground hover:bg-muted rounded-lg data-[state=open]:rounded-b-none gap-2">
              <div className="flex items-center gap-2">
                <span>Reasoning</span>
                {recPill && <Badge variant={recPill.variant}>{recPill.text}</Badge>}
              </div>
            </AccordionTrigger>
            <AccordionContent className="px-3 pb-3">
              <div className="mt-1 max-h-60 overflow-y-auto rounded-md border bg-muted/40 px-3 py-2 text-sm text-foreground [&_strong]:font-semibold [&_strong]:text-foreground [&_p]:my-1 [&_ul]:my-1 [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:my-1 [&_ol]:list-decimal [&_ol]:pl-5 [&_li]:my-1 [&_li]:marker:text-muted-foreground">
                {reasoning}
              </div>

              <div className="mt-3 flex items-center justify-end gap-2">
                <Button
                  type="button"
                  variant={feedbackAnswer === 'yes' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setFeedbackAnswer('yes')}
                  className="h-7 w-7 p-0"
                  aria-label="Thumbs up"
                  title="Thumbs up"
                >
                  <ThumbsUp className="w-3.5 h-3.5" />
                </Button>
                <Button
                  type="button"
                  variant={feedbackAnswer === 'no' ? 'destructive' : 'outline'}
                  size="sm"
                  onClick={() => setFeedbackAnswer('no')}
                  className="h-7 w-7 p-0"
                  aria-label="Thumbs down"
                  title="Thumbs down"
                >
                  <ThumbsDown className="w-3.5 h-3.5" />
                </Button>
              </div>

              {feedbackAnswer !== null && (
                <div className="mt-3 space-y-2">
                  <Textarea
                    value={feedbackNote}
                    onChange={e => setFeedbackNote(e.target.value)}
                    placeholder="Add an optional note..."
                    className="min-h-[80px] text-sm"
                  />
                  <div className="flex justify-end gap-2">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="h-7 text-xs"
                      onClick={() => {
                        setFeedbackAnswer(null);
                        setFeedbackNote('');
                      }}
                    >
                      Cancel
                    </Button>
                    <Button
                      type="button"
                      size="sm"
                      className="h-7 text-xs"
                      onClick={() => {
                        onSubmitFeedback?.(feedbackAnswer, feedbackNote);
                        setFeedbackAnswer(null);
                        setFeedbackNote('');
                      }}
                    >
                      Submit
                    </Button>
                  </div>
                </div>
              )}
            </AccordionContent>
          </AccordionItem>
        </Accordion>
      )}
      {onToggleChangedOnly && (
        <div className="inline-flex items-center rounded-md bg-muted p-0.5 text-muted-foreground">
          <button
            type="button"
            onClick={() => {
              if (showChangedOnly) onToggleChangedOnly();
            }}
            className={`inline-flex items-center h-5 rounded-sm px-2 text-xs font-medium transition-all cursor-pointer ${
              !showChangedOnly ? 'bg-background text-foreground shadow-sm' : ''
            }`}
            aria-pressed={!showChangedOnly}
          >
            All fields
          </button>
          <button
            type="button"
            disabled={changedFieldCount === 0}
            onClick={() => {
              if (!showChangedOnly) onToggleChangedOnly();
            }}
            className={`inline-flex items-center h-5 rounded-sm px-2 text-xs font-medium transition-all ${
              changedFieldCount === 0
                ? 'cursor-not-allowed opacity-50'
                : showChangedOnly
                  ? 'bg-background text-foreground shadow-sm cursor-pointer'
                  : 'cursor-pointer'
            }`}
            aria-pressed={showChangedOnly}
          >
            Changed fields {changedFieldCount}
          </button>
        </div>
      )}
      <Accordion type="multiple" value={expanded} onValueChange={setExpanded} className="space-y-3">
        {/* Groups are skipped in place, never filtered out, so groupIndex stays the key
            the `expanded` state was built from. */}
        {groups.map(
          (group, groupIndex) =>
            !hasOnlyHiddenFields(group) && (
              <AccordionItem
                key={groupIndex}
                value={groupIndex.toString()}
                className="bg-card border rounded-lg"
              >
                <AccordionTrigger className="px-3 py-3 text-sm font-semibold text-foreground hover:bg-muted rounded-lg data-[state=open]:rounded-b-none gap-2">
                  {group.label}
                </AccordionTrigger>
                <AccordionContent className="px-3 pb-3">
                  <div className="mt-1">
                    {group.fields
                      .filter(field => !field.hidden)
                      .map(field => (
                        <AuditorFieldRow
                          key={field.id}
                          field={field}
                          value={getValue(field.id)}
                          correction={getCorrection(field.id)}
                          leafCorrections={getLeafCorrections?.(field.id)}
                          snapshotBacked={snapshotBacked}
                          fieldEnumMap={fieldEnumMap}
                          workflowType={workflowType}
                          schemaGroups={labelGroups}
                          onRevertField={onRevertField}
                          isFieldReverted={isFieldReverted}
                          hideLabel={hasRedundantHeading(group)}
                          // Indexed to this field's subtree exactly as FieldGroupAccordion
                          // indexes them, so the nested leaves resolve the same annotations.
                          outputMetaNode={
                            outputMetadata?.fields?.[field.id] as
                              | Record<string, unknown>
                              | undefined
                          }
                          extractedDataNode={
                            extractedData?.[field.id] as Record<string, unknown> | undefined
                          }
                          extractedMetaNode={
                            extractedMetadata?.fields?.[field.id] as
                              | Record<string, unknown>
                              | undefined
                          }
                          currentCallId={currentCallId}
                          onNavigateToCall={onNavigateToCall}
                          onNavigateToAudit={onNavigateToAudit}
                        />
                      ))}
                  </div>
                </AccordionContent>
              </AccordionItem>
            ),
        )}
      </Accordion>
    </div>
  );
};
